/**
 * Deterministic RUNTIME level generator — the endless engine.
 *
 * This is the answer to "how do we ship 10,000 — or 1,000,000 — levels?".
 * Instead of storing level data, we generate level N on demand from a seed
 * derived from N, validate it against the real simulator (exactly one
 * breachable solution), and cache it. Storage is O(1); the level count is just
 * a number. Generation is deterministic, so "level 5000" is identical every
 * session and a player's progress stays meaningful.
 *
 * WHAT MAKES IT NOT BORING (the 2026-06 overhaul):
 * The old generator had ONE level shape (a flat top→bottom fan) and a curve so
 * slow that the first few hundred levels were all tiny beginner boards. This
 * version is an ARCHETYPE ENGINE: every level is built by one of several
 * structurally distinct generators (flat fan, tall lane gauntlet, minefield,
 * splitter cascade, and periodic BOSS levels), chosen deterministically per
 * index, on a difficulty curve that ramps fast and never fully caps. Each
 * archetype is solution-first: it builds a guaranteed winner, attaches decoys
 * that each provably fail, and the validator brute-forces every breach point to
 * confirm exactly one solution. Size, decoy density, thresholds and mechanic
 * richness all scale with depth, so level 5 and level 5000 feel like different
 * games.
 */
import { simulate } from '../sim/simulator';
import { isBreachable } from '../sim/types';
import type { LevelDef, NodeDef, WireDef, NodeType, WireType } from '../sim/types';

type Tier = LevelDef['tier'];

// ── seeded RNG (mulberry32) ───────────────────────────────────────────────────
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
// hash an index into a well-distributed 32-bit seed
function hashIndex(i: number): number {
  let h = (i ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

// ── grid: distinct cells guarantee >=240px spacing & in-bounds placement ─────
// Larger, harder levels use more columns/rows; the camera rig pans/zooms to fit.
// Columns are 240px apart and rows 300px apart — both comfortably above the
// 195px minimum — so any layout that places nodes on distinct cells is spacing-
// safe by construction. ROWS run deep so tall "gauntlet" mazes have room.
const ALL_COLS = [180, 420, 660, 900, 1140, 1380, 1620, 1860];
const ALL_ROWS = [
  380, 680, 980, 1280, 1580, 1880, 2180, 2480,
  2780, 3080, 3380, 3680, 3980, 4280,
];

interface Builder {
  nodes: NodeDef[];
  wires: WireDef[];
  used: Set<string>;
  wid: number;
  cols: number[];
  rows: number[];
}
function newBuilder(nCols: number, nRows: number): Builder {
  return {
    nodes: [], wires: [], used: new Set(), wid: 0,
    cols: ALL_COLS.slice(0, Math.max(2, Math.min(ALL_COLS.length, nCols))),
    rows: ALL_ROWS.slice(0, Math.max(2, Math.min(ALL_ROWS.length, nRows))),
  };
}
function freeColInRow(b: Builder, r: number): number {
  for (let c = 0; c < b.cols.length; c++) if (!b.used.has(`${r},${c}`)) return c;
  return -1;
}
function place(b: Builder, id: string, r: number, c: number, type: NodeType, extra: Partial<NodeDef> = {}): NodeDef {
  const key = `${r},${c}`;
  if (b.used.has(key)) throw new Error('cell collision');
  if (r < 0 || r >= b.rows.length || c < 0 || c >= b.cols.length) throw new Error('out of grid');
  b.used.add(key);
  const n: NodeDef = { id, x: b.cols[c], y: b.rows[r], type, label: id.toUpperCase(), ...extra };
  b.nodes.push(n);
  return n;
}
function wire(b: Builder, a: string, bb: string, type: WireType = 'directional', extra: Partial<WireDef> = {}): WireDef {
  const w: WireDef = { id: `w${b.wid++}`, a, b: bb, type, ...extra };
  b.wires.push(w);
  return w;
}

const pick = <T,>(r: () => number, arr: T[]): T => arr[Math.floor(r() * arr.length)];
function shuffle<T>(r: () => number, arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// ── per-tier knobs for the STANDARD archetype ────────────────────────────────
interface TierCfg {
  starters: [number, number];
  requireds: [number, number];
  trap: ('miss' | 'protected' | 'forbidden')[];
  splitter: boolean;
  shield: boolean;
  core: boolean;
  relay: boolean;     // relay opens a locked wire to a gated required
  capacitor: boolean; // timing gate: direct + capacitor-delayed pulse converge
  cols: number;       // grid width
  rows: number;       // grid height
}
const TIERS: Record<Tier, TierCfg> = {
  tutorial:  { starters: [2, 2], requireds: [1, 1], trap: ['miss'], splitter: false, shield: false, core: false, relay: false, capacitor: false, cols: 4, rows: 5 },
  beginner:  { starters: [2, 3], requireds: [1, 2], trap: ['miss'], splitter: false, shield: false, core: false, relay: false, capacitor: false, cols: 4, rows: 5 },
  easy:      { starters: [2, 3], requireds: [2, 2], trap: ['miss', 'protected'], splitter: false, shield: false, core: false, relay: false, capacitor: false, cols: 4, rows: 5 },
  medium:    { starters: [3, 3], requireds: [2, 3], trap: ['protected', 'miss'], splitter: true, shield: false, core: false, relay: true, capacitor: false, cols: 4, rows: 5 },
  hard:      { starters: [3, 4], requireds: [2, 3], trap: ['protected', 'forbidden', 'miss'], splitter: true, shield: false, core: false, relay: true, capacitor: true, cols: 5, rows: 5 },
  expert:    { starters: [3, 4], requireds: [2, 3], trap: ['protected', 'forbidden', 'miss'], splitter: true, shield: true, core: false, relay: true, capacitor: true, cols: 5, rows: 6 },
  brutal:    { starters: [4, 4], requireds: [2, 3], trap: ['protected', 'forbidden', 'miss'], splitter: true, shield: true, core: false, relay: true, capacitor: true, cols: 5, rows: 6 },
  nightmare: { starters: [4, 4], requireds: [1, 2], trap: ['protected', 'forbidden', 'miss'], splitter: true, shield: true, core: true, relay: true, capacitor: true, cols: 6, rows: 7 },
};

interface GenCtx {
  r: () => number;
  diff: number;   // 0..1 mechanic/threshold intensity for this index
  size: number;   // 0..1 board-size knob
  index: number;
}

// Overrides let the minefield / cascade / boss archetypes reuse the proven
// STANDARD builder while forcing a particular flavor.
interface Over {
  trap?: TierCfg['trap'];
  relay?: boolean; capacitor?: boolean; shield?: boolean; core?: boolean; splitter?: boolean;
  starterBonus?: number;
  forceSplitter?: boolean;  // splitter is always present (cascade)
  cascade?: boolean;        // amp the splitter to factor-3 / threshold-3
  sizeBoost?: number;
}

// ── ARCHETYPE 1: STANDARD ────────────────────────────────────────────────────
// The flat fan: starters across the top, requireds across the bottom, one winner
// whose cascade satisfies every objective while every decoy provably fails.
// Optionally laced with relay / capacitor / shield / core / splitter mechanics.
function buildStandard(tier: Tier, ctx: GenCtx, over: Over = {}): LevelDef | null {
  const base = TIERS[tier];
  const cfg: TierCfg = {
    ...base,
    trap: over.trap ?? base.trap,
    splitter: over.splitter ?? base.splitter,
    shield: over.shield ?? base.shield,
    core: over.core ?? base.core,
    relay: over.relay ?? base.relay,
    capacitor: over.capacitor ?? base.capacitor,
  };
  const r = ctx.r;
  const size = clamp(ctx.size + (over.sizeBoost ?? 0), 0, 1);

  const extraCols = Math.round(size * (ALL_COLS.length - cfg.cols));
  const extraRows = Math.round(size * (Math.min(8, ALL_ROWS.length) - cfg.rows));
  const nCols = Math.min(ALL_COLS.length, cfg.cols + extraCols);
  const nRows = Math.min(ALL_ROWS.length, cfg.rows + extraRows);
  const b = newBuilder(nCols, nRows);
  const lastRow = b.rows.length - 1;
  const colIdx = b.cols.map((_, i) => i);

  const startBoost = Math.round(size * 3) + (over.starterBonus ?? 0);
  const reqBoost = Math.round(size * 2);
  const nStart = Math.min(
    b.cols.length,
    cfg.starters[0] + startBoost + Math.floor(r() * (cfg.starters[1] - cfg.starters[0] + 1)),
  );
  const nReq = Math.min(
    Math.max(1, b.cols.length - 1),
    cfg.requireds[0] + reqBoost + Math.floor(r() * (cfg.requireds[1] - cfg.requireds[0] + 1)),
  );

  const startCols = shuffle(r, colIdx).slice(0, Math.min(nStart, b.cols.length)).sort((a, c) => a - c);
  const winnerCol = pick(r, startCols);
  const starters: string[] = [];
  startCols.forEach((c, i) => { place(b, 's' + (i + 1), 0, c, 'normal'); starters.push('s' + (i + 1)); });
  const winnerId = 's' + (startCols.indexOf(winnerCol) + 1);

  const reqCols = shuffle(r, colIdx).slice(0, Math.min(nReq, b.cols.length)).sort((a, c) => a - c);
  const requireds: string[] = [];
  reqCols.forEach((c, i) => { place(b, 'r' + (i + 1), lastRow, c, 'required'); requireds.push('r' + (i + 1)); });

  // ── relay gate: one required is reachable ONLY by firing a relay that opens
  // a locked wire to it. The winner trips the relay; decoys never reach it. ──
  const useRelay = cfg.relay && requireds.length >= 2 && r() > 0.45;
  const lockedReq = useRelay ? requireds[requireds.length - 1] : null;
  const fanoutReqs = lockedReq ? requireds.filter(x => x !== lockedReq) : requireds;
  if (lockedReq) {
    try {
      let relR = 1, relC = freeColInRow(b, 1);
      if (relC < 0) { relR = 2; relC = freeColInRow(b, 2); }
      if (relC < 0) return null;
      place(b, 'rel', relR, relC, 'relay', { unlocks: ['lkr'] });
      wire(b, winnerId, 'rel');
      b.wires.push({ id: 'lkr', a: 'rel', b: lockedReq, type: 'locked' });
    } catch { return null; }
  }

  const hubCol = pick(r, colIdx.filter(c => c !== winnerCol).concat([winnerCol]));
  // Convergence on the primary required is one of: capacitor timing gate
  // (an early direct pulse + a capacitor-delayed pulse must both land inside a
  // decay window) OR a splitter (two simultaneous pulses) OR a plain hit.
  const splitterGate = over.forceSplitter ? -1 : 0.35;
  const useCap = !over.forceSplitter && cfg.capacitor && fanoutReqs.length >= 1 && r() > 0.6 && freeColInRow(b, 2) >= 0;
  try {
    if (useCap) {
      const tReq = fanoutReqs[0];
      const node = b.nodes.find(n => n.id === tReq)!;
      node.threshold = 2;
      node.decay = 5; // window > the 3-tick gap between the direct and delayed pulse
      wire(b, winnerId, tReq, 'directional');         // early pulse  (arrives t1)
      const capC = freeColInRow(b, 2);
      place(b, 'cap', 2, capC, 'capacitor');
      wire(b, winnerId, 'cap', 'directional');         // cap collapses t1
      wire(b, 'cap', tReq, 'delay');                   // late pulse   (arrives t4)
      for (const req of fanoutReqs.slice(1)) wire(b, winnerId, req);
    } else if (cfg.splitter && fanoutReqs.length >= 1 && r() > splitterGate && freeColInRow(b, 2) >= 0) {
      const sc = b.used.has(`2,${hubCol}`) ? freeColInRow(b, 2) : hubCol;
      const splitFactor = over.cascade ? 3 : 2;
      place(b, 'sp', 2, sc, 'splitter', { splitFactor });
      wire(b, winnerId, 'sp');
      const tReq = fanoutReqs[0];
      b.nodes.find(n => n.id === tReq)!.threshold = over.cascade ? 3 : 2;
      wire(b, 'sp', tReq);
      for (const req of fanoutReqs.slice(1)) wire(b, winnerId, req);
    } else {
      for (const req of fanoutReqs) wire(b, winnerId, req);
    }
  } catch { return null; }

  if (cfg.core) {
    try {
      const coreC = freeColInRow(b, lastRow);
      if (coreC < 0) return null;
      place(b, 'core', lastRow, coreC, 'core', { threshold: 3 });
      const spR = lastRow - 1, spC = freeColInRow(b, spR);
      if (spC < 0) return null;
      place(b, 'csp', spR, spC, 'splitter', { splitFactor: 3 });
      const shC = freeColInRow(b, 1);
      if (shC < 0) return null;
      place(b, 'csh', 1, shC, 'shield', { shields: ['core'] });
      wire(b, winnerId, 'csh');
      wire(b, winnerId, 'csp');
      wire(b, 'csp', 'core');
    } catch { return null; }
  }

  // Shield-pierce on a fan-out required (not the relay-gated one).
  if (cfg.shield && !cfg.core && !lockedReq && fanoutReqs.length >= 1) {
    try {
      const target = fanoutReqs[fanoutReqs.length - 1];
      const shC = freeColInRow(b, 1);
      if (shC >= 0) {
        place(b, 'sh', 1, shC, 'shield', { shields: [target] });
        wire(b, winnerId, 'sh');
        wire(b, winnerId, target, 'delay');
      }
    } catch { /* shield optional */ }
  }

  // Decoys target only the fan-out requireds — the relay-gated required stays
  // pristine (reachable solely through the relay).
  const decoyTargets = fanoutReqs.length > 0 ? fanoutReqs : requireds;
  let protCount = 0;
  for (const s of starters) {
    if (s === winnerId) continue;
    const mode = pick(r, cfg.trap);
    try {
      if (mode === 'protected' && freeColInRow(b, 1) >= 0) {
        place(b, 'p' + (++protCount), 1, freeColInRow(b, 1), 'protected');
        wire(b, s, 'p' + protCount);
      } else if (mode === 'forbidden') {
        wire(b, s, pick(r, decoyTargets), 'forbidden');
      } else {
        wireMiss(b, s, decoyTargets, r);
      }
    } catch { return null; }
  }

  return {
    id: 'GEN', name: 'GEN', tier,
    nodes: b.nodes, wires: b.wires,
    objective: { requiredNodeIds: requireds, ...(cfg.core ? { coreNodeId: 'core' } : {}) },
  };
}

function wireMiss(b: Builder, s: string, requireds: string[], r: () => number) {
  if (requireds.length <= 1) {
    const c = freeColInRow(b, 1);
    if (c < 0) return;
    const sink = 'x' + b.wid;
    place(b, sink, 1, c, 'normal');
    wire(b, s, sink);
    return;
  }
  for (const req of shuffle(r, requireds).slice(0, requireds.length - 1)) wire(b, s, req);
}

// ── ARCHETYPE 2: GAUNTLET ────────────────────────────────────────────────────
// A tall maze of parallel vertical lanes — a totally different feel from the
// flat fan. The trick that makes it a real UNIQUE-SOLUTION puzzle (rather than
// "tap anywhere on the winning column"): the winning starter splits into TWO
// lanes that both descend and converge on a single required with threshold 2.
// Only the starter feeds both lanes, so only the starter delivers the 2 pulses
// the required needs. Tapping any node partway down a lane delivers just one
// pulse → under threshold → fail. Every decoy lane dead-ends short, terminates
// in a protected node, or trips a forbidden alarm. The puzzle is to trace, lane
// by lane, which thread is the one true source.
function buildGauntlet(ctx: GenCtx): LevelDef | null {
  const { r, diff, size } = ctx;
  const nDecoys = clamp(2 + Math.floor(size * 3), 2, 5);
  const totalCols = clamp(2 + nDecoys, 4, ALL_COLS.length); // winner owns 2 cols
  const depth = clamp(4 + Math.floor(size * 4), 4, 8);
  const b = newBuilder(totalCols, depth);
  const bottom = depth - 1;
  const cols = shuffle(r, b.cols.map((_, i) => i));
  const cL = cols[0];
  const cR = cols[1];
  const decoyCols = cols.slice(2);
  const requiredId = 'r1';

  try {
    // ── WINNER: one breach (s0) feeding two converging lanes ──
    place(b, 's0', 0, cL, 'normal');
    let prevL = 's0';
    for (let row = 1; row < bottom; row++) {
      const id = `L${row}`; place(b, id, row, cL, 'normal'); wire(b, prevL, id, 'directional'); prevL = id;
    }
    let prevR = 's0';
    for (let row = 1; row < bottom; row++) {
      const id = `R${row}`; place(b, id, row, cR, 'normal'); wire(b, prevR, id, 'directional'); prevR = id;
    }
    place(b, requiredId, bottom, cL, 'required', { threshold: 2 });
    wire(b, prevL, requiredId, 'directional');
    wire(b, prevR, requiredId, 'directional');

    // ── DECOYS: single lanes that never legitimately reach the required ──
    decoyCols.forEach((c, di) => {
      const sId = `d${di}s`;
      place(b, sId, 0, c, 'normal');
      let prev = sId;
      for (let row = 1; row < bottom; row++) {
        const id = `d${di}_${row}`; place(b, id, row, c, 'normal'); wire(b, prev, id, 'directional'); prev = id;
      }
      const modes: ('miss' | 'protected' | 'forbidden')[] = diff > 0.35
        ? ['miss', 'protected', 'forbidden']
        : ['miss', 'protected'];
      const mode = pick(r, modes);
      const dead = `d${di}x`;
      place(b, dead, bottom, c, mode === 'protected' ? 'protected' : 'normal');
      wire(b, prev, dead, 'directional');
      if (mode === 'forbidden') {
        // tapping this lane's HEAD trips an alarm immediately — guaranteed fail.
        wire(b, sId, requiredId, 'forbidden');
      }
    });
  } catch { return null; }

  return {
    id: 'GEN', name: 'GEN', tier: tierForDiff(Math.max(diff, 0.2)),
    nodes: b.nodes, wires: b.wires,
    objective: { requiredNodeIds: [requiredId] },
  };
}

// ── ARCHETYPE 3: RELAY CHAIN ─────────────────────────────────────────────────
// A sequence of relays daisy-chained by LOCKED wires. The winning starter trips
// the first relay, which opens the locked wire to the second, which opens the
// locked wire to the third… and only the last relay's locked wire reaches the
// required. Watching the dashed wires light up one by one is a distinct "crack
// the sequence" feel. It is a unique-solution puzzle by construction: the chain
// is reachable only from the one starter wired into relay #1, and every relay /
// required is non-breachable, so no other node can win. Decoys dead-end, hit a
// protected node, or trip a forbidden alarm.
function buildRelayChain(ctx: GenCtx): LevelDef | null {
  const { r, diff, size } = ctx;
  const nLinks = clamp(2 + Math.floor(diff * 2 + size), 2, 4);
  const nRows = clamp(nLinks + 2, 4, ALL_ROWS.length);
  const nDecoys = clamp(1 + Math.floor(size * 3), 1, 4);
  const nCols = clamp(2 + nDecoys, 3, ALL_COLS.length);
  const b = newBuilder(nCols, nRows);
  const bottom = nRows - 1;
  const cols = shuffle(r, b.cols.map((_, i) => i));
  const chainCol = cols[0];
  const decoyCols = cols.slice(1, 1 + nDecoys);
  const requiredId = 'r1';

  try {
    place(b, 's0', 0, chainCol, 'normal');
    let prev = 's0';
    for (let i = 1; i <= nLinks; i++) {
      const relId = 'rel' + i;
      place(b, relId, i, chainCol, 'relay', { unlocks: ['lk' + i] });
      if (i === 1) wire(b, prev, relId, 'directional'); // s0 → rel1 (open)
      else b.wires.push({ id: 'lk' + (i - 1), a: prev, b: relId, type: 'locked' });
      prev = relId;
    }
    place(b, requiredId, bottom, chainCol, 'required');
    b.wires.push({ id: 'lk' + nLinks, a: prev, b: requiredId, type: 'locked' });

    decoyCols.forEach((c, di) => {
      const sId = 'e' + di;
      place(b, sId, 0, c, 'normal');
      const modes: ('protected' | 'forbidden' | 'miss')[] = diff > 0.3
        ? ['protected', 'forbidden', 'miss'] : ['protected', 'miss'];
      const mode = pick(r, modes);
      if (mode === 'forbidden') {
        wire(b, sId, requiredId, 'forbidden');
        place(b, 'ed' + di, 1, c, 'normal'); wire(b, sId, 'ed' + di, 'directional');
      } else if (mode === 'protected') {
        place(b, 'ep' + di, 1, c, 'protected'); wire(b, sId, 'ep' + di, 'directional');
      } else {
        let p = sId;
        for (let row = 1; row <= Math.min(2, bottom - 1); row++) {
          const id = 'em' + di + '_' + row; place(b, id, row, c, 'normal'); wire(b, p, id, 'directional'); p = id;
        }
      }
    });
  } catch { return null; }

  return {
    id: 'GEN', name: 'GEN', tier: tierForDiff(Math.max(diff, 0.4)),
    nodes: b.nodes, wires: b.wires,
    objective: { requiredNodeIds: [requiredId] },
  };
}

// ── difficulty curve ──────────────────────────────────────────────────────────
const TIER_ORDER: Tier[] = ['beginner', 'easy', 'medium', 'hard', 'expert', 'brutal', 'nightmare'];

/** Mechanic/threshold intensity 0..1. Fast early ramp (medium by ~level 60,
 *  hard by ~120, nightmare territory by ~240), then asymptotes near 1. */
export function diffForIndex(index: number): number {
  return 1 - Math.exp(-index / 110);
}
/** Board-size knob. Keeps widening boards until ~level 1200, then stays maxed —
 *  the deeper you go the more lanes/decoys you have to scan. */
export function sizeForIndex(index: number): number {
  return clamp(index / 1200, 0, 1);
}
/** Kept for back-compat with older callers. */
export function complexityForIndex(index: number): number {
  return sizeForIndex(index);
}

const TIER_BY_DIFF: [number, Tier][] = [
  [0.08, 'tutorial'], [0.18, 'beginner'], [0.32, 'easy'], [0.48, 'medium'],
  [0.64, 'hard'], [0.79, 'expert'], [0.91, 'brutal'],
];
function tierForDiff(d: number): Tier {
  for (const [lt, tier] of TIER_BY_DIFF) if (d < lt) return tier;
  return 'nightmare';
}
export function tierForIndex(index: number): Tier {
  return tierForDiff(diffForIndex(index));
}

// ── archetype selection ───────────────────────────────────────────────────────
type Archetype = 'standard' | 'gauntlet' | 'minefield' | 'cascade' | 'relaychain' | 'boss';

const BOSS_EVERY = 20;
const MEGABOSS_EVERY = 100;
/** Every BOSS_EVERY-th level (from level 20 on) is a boss. */
export function isBossIndex(index: number): boolean {
  return index >= BOSS_EVERY - 1 && (index + 1) % BOSS_EVERY === 0;
}
/** Every MEGABOSS_EVERY-th level is a bigger, meaner boss. */
export function isMegaBossIndex(index: number): boolean {
  return index >= MEGABOSS_EVERY - 1 && (index + 1) % MEGABOSS_EVERY === 0;
}

function archetypeForIndex(index: number): Archetype {
  if (isBossIndex(index)) return 'boss';
  const unlocked: Archetype[] = ['standard'];
  if (index >= 2) unlocked.push('gauntlet');
  if (index >= 7) unlocked.push('minefield');
  if (index >= 12) unlocked.push('cascade');
  if (index >= 22) unlocked.push('relaychain');
  // Deterministic rotation, perturbed by a hash, so neighbours differ but the
  // sequence is stable per index.
  const r = rng(hashIndex(index * 131 + 7));
  return unlocked[(index + Math.floor(r() * unlocked.length)) % unlocked.length];
}

/** Try the chosen archetype first, then fall back to proven builders so a hard
 *  index never collapses to the trivial fallback. */
function fallbackOrder(a: Archetype): Archetype[] {
  switch (a) {
    case 'boss': return ['boss', 'standard'];
    case 'gauntlet': return ['gauntlet', 'standard'];
    case 'minefield': return ['minefield', 'standard'];
    case 'cascade': return ['cascade', 'standard'];
    case 'relaychain': return ['relaychain', 'standard'];
    default: return ['standard'];
  }
}
const ARCH_SALT: Record<Archetype, number> = {
  standard: 0x11111, gauntlet: 0x22222, minefield: 0x33333,
  cascade: 0x44444, relaychain: 0x66666, boss: 0x55555,
};

function buildArchetype(a: Archetype, ctx: GenCtx): LevelDef | null {
  switch (a) {
    case 'gauntlet':
      return buildGauntlet(ctx);
    case 'relaychain':
      return buildRelayChain(ctx);
    case 'minefield':
      // dense danger: every decoy ends in red, on a heavier board.
      return buildStandard(tierForDiff(Math.max(ctx.diff, 0.34)), ctx, {
        trap: ['forbidden', 'protected'],
        starterBonus: 1,
        sizeBoost: 0.15,
      });
    case 'cascade':
      // vertical amplification: a forced factor-3 splitter overflows a
      // threshold-3 required. No relay/cap clutter.
      return buildStandard(tierForDiff(Math.max(ctx.diff, 0.42)), ctx, {
        forceSplitter: true, cascade: true, splitter: true,
        relay: false, capacitor: false, core: false, shield: false,
        sizeBoost: 0.1,
      });
    case 'boss': {
      // the full machine, maxed: nightmare tier, every mechanic enabled, extra
      // decoys, biggest board. Mega-bosses (every 100) go even larger.
      const mega = isMegaBossIndex(ctx.index);
      return buildStandard('nightmare', { ...ctx, size: 1 }, {
        relay: true, capacitor: true, shield: true, core: true, splitter: true,
        starterBonus: mega ? 2 : 1,
      });
    }
    default:
      return buildStandard(tierForDiff(ctx.diff), ctx);
  }
}

// ── validators ────────────────────────────────────────────────────────────────
function uniqueWinner(level: LevelDef): boolean {
  let count = 0;
  for (const n of level.nodes) {
    if (!isBreachable(n)) continue;
    if (simulate(level, n.id).outcome === 'win') { count++; if (count > 1) return false; }
  }
  return count === 1;
}
function spacingOk(level: LevelDef): boolean {
  for (let i = 0; i < level.nodes.length; i++)
    for (let j = i + 1; j < level.nodes.length; j++) {
      const a = level.nodes[i], c = level.nodes[j];
      if (Math.hypot(a.x - c.x, a.y - c.y) < 195) return false;
    }
  return true;
}

const NAME_POOL = [
  'Relay', 'Cinder', 'Vault', 'Specter', 'Halcyon', 'Quartz', 'Vector', 'Onyx',
  'Pylon', 'Cascade', 'Ledger', 'Mirage', 'Bastion', 'Cipher', 'Lattice', 'Nexus',
  'Falter', 'Static', 'Ember', 'Drift', 'Conduit', 'Fracture', 'Helix', 'Voltage',
  'Sever', 'Tessel', 'Gambit', 'Ridge', 'Pulse', 'Threshold', 'Aperture', 'Echo',
  'Sable', 'Kestrel', 'Vellum', 'Strata', 'Cobalt', 'Rift', 'Talon', 'Vesper',
];
const BOSS_NAMES = [
  'Leviathan', 'Obelisk', 'Tribunal', 'Hydra', 'Citadel', 'Maelstrom',
  'Colossus', 'Pantheon', 'Singularity', 'Warden', 'Behemoth', 'Eclipse',
];

/**
 * A one-line, in-context reminder of the trickiest mechanic present in a level.
 * Surfaced under the HUD title so a player who has forgotten what a node does
 * gets a nudge every level.
 */
function hintForLevel(level: LevelDef): string {
  const types = new Set(level.nodes.map(n => n.type));
  const wires = new Set(level.wires.map(w => w.type ?? 'normal'));
  if (level.objective.coreNodeId) return 'Crash the CORE, flood it past its threshold with every shield down.';
  if (types.has('shield')) return 'Green SHIELDS soak hits until you collapse them. Drop the guard first.';
  if (types.has('relay')) return 'A RELAY opens locked (dashed) wires when it falls. Trip it to reach what is sealed.';
  if (types.has('capacitor')) return 'A CAPACITOR holds its pulse, use the delay to land a second hit in time.';
  if (types.has('splitter')) return 'A SPLITTER multiplies one pulse into many. Feed a node that needs more than one.';
  if (wires.has('forbidden')) return 'Dashed RED wires are alarms, touch one and the run ends.';
  if (types.has('protected')) return 'RED nodes must never be hit. Find a breach whose cascade avoids them.';
  return 'Find the one breach point whose cascade collapses every required node.';
}

const cache = new Map<number, LevelDef>();

/**
 * Core synthesis loop, shared by endless and daily. Tries the chosen archetype
 * (then its fallbacks), re-seeding and gradually relaxing board size each
 * attempt, until it lands a spacing-safe, unique-solution layout.
 */
function synthesize(
  arche: Archetype, diff: number, size: number, base: number, index: number,
): { level: LevelDef; arche: Archetype } | null {
  for (const a of fallbackOrder(arche)) {
    for (let attempt = 0; attempt < 260; attempt++) {
      const r = rng((base + attempt * 0x9e37 + ARCH_SALT[a]) >>> 0);
      const relax = size * Math.max(0.35, 1 - attempt / 160);
      const ctx: GenCtx = { r, diff, size: relax, index };
      let cand: LevelDef | null = null;
      try { cand = buildArchetype(a, ctx); } catch { cand = null; }
      if (cand && spacingOk(cand) && uniqueWinner(cand)) return { level: cand, arche: a };
    }
  }
  return null;
}

/**
 * Generate (or recall) the procedural level at the given endless index.
 * Deterministic and cached. Always returns a unique-solution level.
 */
export function generateLevel(index: number): LevelDef {
  const cached = cache.get(index);
  if (cached) return cached;

  const diff = diffForIndex(index);
  const size = sizeForIndex(index);
  const arche = archetypeForIndex(index);
  const syn = synthesize(arche, diff, size, hashIndex(index), index);
  let level: LevelDef | null = syn?.level ?? null;
  const usedArche: Archetype = syn?.arche ?? arche;

  // Fallback: a trivial but valid level (should never be needed in practice).
  if (!level) {
    level = {
      id: 'GEN', name: 'GEN', tier: 'beginner',
      nodes: [
        { id: 's1', x: 420, y: 380, type: 'normal', label: 'S1' },
        { id: 's2', x: 660, y: 380, type: 'normal', label: 'S2' },
        { id: 'r1', x: 540, y: 1580, type: 'required', label: 'R1' },
      ],
      wires: [{ id: 'w0', a: 's1', b: 'r1', type: 'directional' }],
      objective: { requiredNodeIds: ['r1'] },
    };
  }

  const id = 'p' + index;
  const boss = usedArche === 'boss';
  const mega = boss && isMegaBossIndex(index);
  const name = boss
    ? BOSS_NAMES[hashIndex(index * 7 + 1) % BOSS_NAMES.length]
    : NAME_POOL[hashIndex(index * 7 + 1) % NAME_POOL.length];
  const tier = level.tier;
  const finalized: LevelDef = {
    ...level,
    id,
    name,
    intro: mega
      ? `◆◆ MEGABOSS ◆◆  ·  ${name.toUpperCase()}`
      : boss
        ? `▲ BOSS ▲  ·  ${name.toUpperCase()}`
        : `${name.toUpperCase()}  ·  ${tier.toUpperCase()}`,
    hint: hintForLevel(level),
  };
  cache.set(index, finalized);
  return finalized;
}

/** Codename shown as the big HUD title for a procedural level — the level number.
 *  Boss levels are marked with a star. */
export function proceduralCodename(index: number): string {
  if (isMegaBossIndex(index)) return `LV ${index + 1} ★★`;
  return isBossIndex(index) ? `LV ${index + 1} ★` : `LV ${index + 1}`;
}

/** True if an id refers to a procedural (endless) level. */
export function isProceduralId(id: string): boolean {
  return /^p\d+$/.test(id);
}
export function proceduralIndex(id: string): number {
  return parseInt(id.slice(1), 10);
}

// ── DAILY CHALLENGE ───────────────────────────────────────────────────────────
// One shared puzzle per calendar day, derived deterministically from the date —
// so everyone playing on 2026-06-10 gets the exact same board. Difficulty ramps
// across the week (gentle Sunday → spicy Saturday) and the archetype rotates by
// date, so the daily always feels fresh. Solving it feeds a separate daily
// streak (the Wordle-style retention hook). Lives/ads don't apply — it's a free
// daily brain-teaser you can retry until you crack it.
const dailyCache = new Map<string, LevelDef>();
const DAILY_ARCHES: Archetype[] = ['standard', 'gauntlet', 'minefield', 'cascade', 'relaychain'];

/** FNV-1a hash of a string → 32-bit seed. */
function hashStr(s: string): number {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

/** Today's daily key, `YYYYMMDD` in UTC, so every player worldwide gets the same board at the same moment. */
export function dailyKeyFor(d = new Date()): string {
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
}
/** Yesterday's key (for streak continuity checks). */
export function dailyKeyYesterday(d = new Date()): string {
  return dailyKeyFor(new Date(d.getTime() - 86_400_000));
}
export function isDailyId(id: string): boolean { return /^d\d{8}$/.test(id); }
export function dailyIdFor(key: string): string { return 'd' + key; }
export function dailyKeyFromId(id: string): string { return id.slice(1); }
export function dailyDateLabel(key: string): string {
  return `${key.slice(0, 4)}-${key.slice(4, 6)}-${key.slice(6, 8)}`;
}
/** Big HUD title for a daily level. */
export function dailyCodename(_key: string): string { return 'DAILY'; }

/** Generate (or recall) the daily puzzle for a `YYYYMMDD` key. */
export function generateDailyLevel(key: string): LevelDef {
  const cached = dailyCache.get(key);
  if (cached) return cached;

  const seed = hashStr('daily:' + key);
  const yr = +key.slice(0, 4), mo = +key.slice(4, 6) - 1, dy = +key.slice(6, 8);
  const weekday = new Date(Date.UTC(yr, mo, dy)).getUTCDay(); // 0 Sun .. 6 Sat
  const diff = clamp(0.42 + (weekday / 6) * 0.4, 0, 1);
  const size = clamp(0.25 + (weekday / 6) * 0.3, 0, 1);
  const arche = DAILY_ARCHES[seed % DAILY_ARCHES.length];

  // index `1` is neutral here (only the boss archetype reads index, and daily
  // never picks boss).
  let level = synthesize(arche, diff, size, seed, 1)?.level
    ?? synthesize('standard', diff * 0.6, size * 0.5, seed ^ 0x1234, 1)?.level
    ?? null;
  if (!level) {
    level = {
      id: 'GEN', name: 'GEN', tier: 'beginner',
      nodes: [
        { id: 's1', x: 420, y: 380, type: 'normal', label: 'S1' },
        { id: 's2', x: 660, y: 380, type: 'normal', label: 'S2' },
        { id: 'r1', x: 540, y: 1580, type: 'required', label: 'R1' },
      ],
      wires: [{ id: 'w0', a: 's1', b: 'r1', type: 'directional' }],
      objective: { requiredNodeIds: ['r1'] },
    };
  }

  const name = NAME_POOL[hashStr('name:' + key) % NAME_POOL.length];
  const finalized: LevelDef = {
    ...level,
    id: dailyIdFor(key),
    name,
    intro: `◆ DAILY ◆  ·  ${dailyDateLabel(key)}`,
    hint: hintForLevel(level),
  };
  dailyCache.set(key, finalized);
  return finalized;
}
